import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  OpenInNew,
  Search,
  AssignmentTurnedIn,
  HourglassTop,
  RadioButtonUnchecked,
  CalendarMonth,
  DoneAllRounded,
  PlayCircleOutlineRounded,
  ViewListRounded,
} from '@mui/icons-material';
import AddAssessmentSession from '../Dialogs/AddAssessmentSession';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(first = '', last = '') {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function stableSort(array, comparator) {
  return [...array]
    .map((el, index) => [el, index])
    .sort((a, b) => {
      const order = comparator(a[0], b[0]);
      return order !== 0 ? order : a[1] - b[1];
    })
    .map(([el]) => el);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  const av = a[orderBy] ?? '';
  const bv = b[orderBy] ?? '';
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

// ─── Session classifier ───────────────────────────────────────────────────────
// Returns one of: 'active' | 'completed' | 'waiting'
function classifySession(s) {
  if (s.completed && s.assessment_student_id) return 'completed';
  if (s.is_active && !s.assessment_student_id)            return 'active';
  return 'waiting';
}

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ session }) {
  const status = classifySession(session);
  if (status === 'completed') {
    return (
      <Chip
        icon={<DoneAllRounded sx={{ fontSize: '13px !important' }} />}
        label="Graded & done"
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
      />
    );
  }
  if (status === 'active') {
    return (
      <Chip
        icon={<HourglassTop sx={{ fontSize: '13px !important' }} />}
        label="In progress"
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
      />
    );
  }
  return (
    <Chip
      icon={<RadioButtonUnchecked sx={{ fontSize: '13px !important' }} />}
      label="Waiting"
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, color: 'text.secondary', borderColor: 'divider' }}
    />
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
const EMPTY_MESSAGES = {
  active:    { icon: PlayCircleOutlineRounded, title: 'No active sessions', body: 'Start a session from any assessment to see it here.' },
  completed: { icon: DoneAllRounded,           title: 'No completed sessions', body: 'Completed and graded sessions will appear here.' },
  all:       { icon: AssignmentTurnedIn,       title: 'No sessions yet',     body: 'Start a session from any assessment below.' },
};

function EmptySessions({ filter }) {
  const { icon: Icon, title, body } = EMPTY_MESSAGES[filter] ?? EMPTY_MESSAGES.all;
  return (
    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 1, color: 'text.secondary', borderRadius: 1, bgcolor: 'action.hover' }}>
      <Icon sx={{ fontSize: 36, opacity: 0.35 }} />
      <Typography variant="body2" fontWeight={500}>{title}</Typography>
      <Typography variant="caption" color="text.disabled" textAlign="center" maxWidth={280}>{body}</Typography>
    </Box>
  );
}

// ─── Column definitions ───────────────────────────────────────────────────────
const COLUMNS = [
  { id: 'student',       label: 'Student',   sortKey: 'last_name',     width: '22%'                },
  { id: 'session_token', label: 'Join code', sortKey: 'session_token', width: '14%', hideBelow: 'sm' },
  { id: 'status',        label: 'Status',    sortKey: 'completed',     width: '16%'                },
  { id: 'created_at',    label: 'Started',   sortKey: 'created_at',    width: '18%', hideBelow: 'md' },
  { id: 'actions',       label: '',          sortKey: null,            width: '8%'                  },
];

const colVisibility = (hideBelow) =>
  hideBelow ? { display: { xs: 'none', [hideBelow]: 'table-cell' } } : {};

// ─── Main component ───────────────────────────────────────────────────────────
const ActiveSessionsTable = ({ sessions = [], onView, loading = false }) => {
  const [order, setOrder]               = useState('desc');
  const [orderBy, setOrderBy]           = useState('created_at');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [search, setSearch]             = useState('');
  const [hostAssessments, setHostAssessments] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'completed' | 'all'

  const handleSort = (sortKey) => {
    if (!sortKey) return;
    setOrder(prev => (orderBy === sortKey && prev === 'asc') ? 'desc' : 'asc');
    setOrderBy(sortKey);
    setPage(0);
  };

  const handleFilterChange = (_, newVal) => {
    if (newVal) { setStatusFilter(newVal); setPage(0); }
  };

  // ── Counts per bucket (unaffected by search) ──────────────────────────────
  const counts = useMemo(() => ({
    active:    sessions.filter(s => classifySession(s) === 'active').length,
    completed: sessions.filter(s => classifySession(s) === 'completed').length,
    all:       sessions.length,
  }), [sessions]);

  // ── Filter by status first, then by search ─────────────────────────────────
  const filtered = useMemo(() => {
    const byStatus = sessions.filter(s => {
      if (statusFilter === 'active')    return classifySession(s) === 'active';
      if (statusFilter === 'completed') return classifySession(s) === 'completed';
      return true; // 'all'
    });

    const q = search.toLowerCase().trim();
    if (!q) return byStatus;
    return byStatus.filter(s =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      (s.join_code || '').toLowerCase().includes(q)
    );
  }, [sessions, statusFilter, search]);

  const sorted    = useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>

      {/* ── Top bar: filter toggles + search + action ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>

        {/* Status filter toggle */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={statusFilter}
          onChange={handleFilterChange}
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="active" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.5, gap: 0.75,
            '&.Mui-selected': { bgcolor: 'warning.main', color: 'warning.contrastText', '&:hover': { bgcolor: 'warning.dark' } } }}>
            <HourglassTop sx={{ fontSize: 14 }} />
            Active
            <Chip label={counts.active} size="small"
              sx={{ height: 16, fontSize: '0.65rem', borderRadius: 1, bgcolor: 'rgba(0,0,0,0.12)', color: 'inherit', fontWeight: 700 }} />
          </ToggleButton>

          <ToggleButton value="completed" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.5, gap: 0.75,
            '&.Mui-selected': { bgcolor: 'success.main', color: 'success.contrastText', '&:hover': { bgcolor: 'success.dark' } } }}>
            <DoneAllRounded sx={{ fontSize: 14 }} />
            Completed
            <Chip label={counts.completed} size="small"
              sx={{ height: 16, fontSize: '0.65rem', borderRadius: 1, bgcolor: 'rgba(0,0,0,0.12)', color: 'inherit', fontWeight: 700 }} />
          </ToggleButton>

          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.5, gap: 0.75,
            '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } } }}>
            <ViewListRounded sx={{ fontSize: 14 }} />
            All
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by name or code…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Assessment session button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<CalendarMonth sx={{ fontSize: 15 }} />}
          onClick={() => setHostAssessments(true)}
          sx={{ fontWeight: 600, fontSize: '0.75rem', borderRadius: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Assessment sessions
        </Button>
      </Stack>

      {/* ── Table or empty ── */}
      {filtered.length === 0 && !loading ? (
        <EmptySessions filter={statusFilter} />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>

              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {COLUMNS.map(col => (
                    <TableCell key={col.id} width={col.width}
                      sortDirection={orderBy === col.sortKey ? order : false}
                      sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary',
                        letterSpacing: '0.05em', py: 1, borderBottom: '1px solid', borderColor: 'divider',
                        textTransform: 'uppercase', whiteSpace: 'nowrap', ...colVisibility(col.hideBelow) }}>
                      {col.sortKey ? (
                        <TableSortLabel
                          active={orderBy === col.sortKey}
                          direction={orderBy === col.sortKey ? order : 'asc'}
                          onClick={() => handleSort(col.sortKey)}
                          sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                      No results match "{search}"
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((session) => {
                    const sessionStatus = classifySession(session);
                    return (
                      <TableRow
                        key={session.id}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          ...(sessionStatus === 'active' && {
                            bgcolor: (t) => t.palette.mode === 'dark' ? 'warning.900' : '#fffde7',
                            '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? 'warning.800' : '#fff9c4' },
                          }),
                          ...(sessionStatus === 'completed' && {
                            bgcolor: (t) => t.palette.mode === 'dark' ? 'success.900' : '#f1f8f1',
                            '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? 'success.800' : '#e8f5e9' },
                          }),
                        }}
                      >
                        {/* ── Student ── */}
                        <TableCell sx={{ py: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: sessionStatus === 'completed' ? 'success.100' : 'primary.100',
                              color: sessionStatus === 'completed' ? 'success.800' : 'primary.800',
                              flexShrink: 0 }}>
                              {getInitials(session.first_name, session.last_name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {session.first_name} {session.last_name}
                              </Typography>
                              {/* Mobile: join code + date inline */}
                              <Stack direction="row" spacing={0.75} alignItems="center"
                                sx={{ display: { xs: 'flex', sm: 'none' }, mt: 0.25 }}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700,
                                  letterSpacing: '0.1em', bgcolor: 'action.selected', px: 0.75, py: 0.15,
                                  borderRadius: 1, color: 'text.primary' }}>
                                  {session.join_code || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" noWrap
                                  sx={{ display: { xs: 'block', md: 'none' } }}>
                                  {formatDate(session.created_at)}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* ── Join code (sm+) ── */}
                        <TableCell sx={{ py: 1, ...colVisibility('sm') }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: '0.12em',
                            fontWeight: 700, fontSize: '0.78rem' }}>
                            {session.join_code || '—'}
                          </Typography>
                        </TableCell>

                        {/* ── Status ── */}
                        <TableCell sx={{ py: 1 }}>
                          <StatusChip session={session} />
                        </TableCell>

                        {/* ── Started (md+) ── */}
                        <TableCell sx={{ py: 1, ...colVisibility('md') }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(session.created_at)}
                          </Typography>
                        </TableCell>

                        {/* ── Actions ── */}
                        <TableCell sx={{ py: 1 }} align="right">
                          <Tooltip title={sessionStatus === 'completed' ? 'Already graded' : 'Open session'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={sessionStatus === 'completed'}
                                onClick={() => onView?.(session)}
                                sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 1, p: '4px',
                                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                              >
                                <OpenInNew sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </>
      )}

      <AddAssessmentSession
        open={hostAssessments}
        onClose={() => setHostAssessments(false)}
        students={[]}
        substitute_id={false}
      />
    </Box>
  );
};

export default ActiveSessionsTable;